<script setup>  
  import { ref, onMounted, getCurrentInstance } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import { usePageLoadingStore } from '../../../../../../stores/pageLoading'
  import { useUserStore } from '../../../../stores/user'
  import JobFairResumeDetailModal from '../../../../components/modal/jobFair/JobFairResumeDetailModal.vue'
  import JobFairJobNoticeDetailModal from '../../../../components/modal/jobFair/JobFairJobNoticeDetailModal.vue'
  import mypageJobFairService from '../../../../services/mypage/jobFair/mypageJobFairService'

  const route = useRoute()
  const router = useRouter()
  const loadingStore = usePageLoadingStore()
  const { user, isAuthenticated } = storeToRefs(useUserStore())
  const { $pageListBoardNo } = getCurrentInstance().appContext.config.globalProperties;

  const globals = getCurrentInstance().appContext.config.globalProperties;
  const { $gbNameKR, $regex, $gb : { EMPLOYEE_NUM_GB, SALES_AMT_GB }, alert, confirm } = globals;

  const currentPage = ref(1);
  const pageSize = ref(10);
  const totalPages = ref(1);
  const totalCount = ref(0);
  const list = ref([]);

  const pageSizeGroup = ref([
    { label:"10개씩 보기", value: 10 },
    { label:"30개씩 보기", value: 30 },
    { label:"50개씩 보기", value: 50 },
    { label:"100개씩 보기", value: 100 },
  ])

  // 모달
  const jobFairResumeModal = ref(null); // 특별채용관 이력서
  const jobNoticeModal = ref(null); // 채용공고

  // 페이징 개수 변경
  const onSelectPageSize = (option) => {
    pageSize.value = option.value;
    currentPage.value = 1;
    retrieveList();
  }

  // 페이지 클릭
  const onSelectPage = (pageNo, depth) => {
    currentPage.value = pageNo;
    retrieveList();
  }

  // 조회
  const retrieveList = () => {
    const paramObj = {
      corpId: user.value?.userInfo?.corporation?.corpId || '',
      pageNumber: currentPage.value || 0,
      size: pageSize.value || 10,
    };
    
    loadingStore.loading = true;
    mypageJobFairService.retrieveCorpInterviewProposalEntryListPaging(paramObj)
      .then( (res) => {
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;
          totalPages.value = result.totalPages;
          totalCount.value = result.totalElements;
          list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
        }
        loadingStore.loading = false;
      })
      .catch( (error) =>  {
        loadingStore.loading = false;
      });
  }

  // 이력서 보기
  const onClickMyResume = (resumeId, delYn) => {
    if (delYn === 'Y') {
      alert(["삭제된 이력서 입니다."]);
    } else {
      jobFairResumeModal.value.show({
        resumeId,
        hideFooterBtn: true,
      });
    }
  }

  // 채용공고 보기
  const onClickJobNotice = async (jobNoticeSeq) => {
    jobNoticeModal.value.show({
      corpId: user.value?.userInfo?.corporation?.corpId || '',
      jobNoticeSeq,
      hideFooterBtn: true,
    })
  }

  // 상태변경
  const onClickIntvPropState = async (resumeId, jobNoticeSeq, stateGb) => {
    let ok, messageContent, okButtonText;
    if(stateGb === '04') {
      messageContent = ["제안을 취소 하시겠습니까?", "'제안취소'로 상태가 변경됩니다."];
      okButtonText = "제안취소";
    } else if(stateGb === '05') {
      messageContent = ["최종합격 처리하시겠습니까?", "'최종합격'으로 상태가 변경됩니다."];
      okButtonText = "최종합격";
    } else if(stateGb === '06') {
      messageContent = ["최종불합격 처리하시겠습니까?", "'최종불합격'으로 상태가 변경됩니다."];
      okButtonText = "최종불합격";
    }

    ok = await confirm(messageContent, okButtonText);
    if(ok) {
      const paramObj = {
        resumeId: resumeId,
        jobNoticeSeq: jobNoticeSeq,
        intvPropStateGb: stateGb,
      };

      mypageJobFairService.updateIntvPropStateGb(paramObj)
        .then( (res) => {
          loadingStore.loading = false;
          if(res.data.code.toLowerCase() === 'success') {
            alert(["변경하였습니다."]);
            retrieveList();
          } else {
            alert(["변경을 실패하였습니다."]);
          }
        })
        .catch( (res) =>  {
          loadingStore.loading = false;
          alert(["변경을 실패하였습니다."]);
        });
    }
  }

  onMounted(()=>{
    retrieveList();
  });
</script>

<template>
  <div class="table-form">
    <div class="list-top">
      <p>총 <span>{{ $number(totalCount)}}</span>개</p>
      <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize"/>
    </div>
    <div class="table-form">
      <table class="table-form board-mobile-vertical">
        <tr>
          <th>번호</th>
          <th>이름</th>
          <th>이력서 제목</th>
          <th>관심 카테고리</th>
          <th>전공계열(주전공)</th>
          <th>면접제안 공고 제목</th>
          <th>제안일</th>
          <th>상태</th>
          <th>상태변경</th>
        </tr>
        <tr v-for="user in list">
          <td>{{ user.boardNo }}</td>
          <td>{{ user.userName }}</td>
          <td>
            <p class="file text-overflow" v-if="user.fileYn === 'Y'" v-for="resumeAttaches in user.resumeAttaches" @click="$downloadForPrivate({fileName:resumeAttaches.fileName, fileDir: resumeAttaches.fileDir })">
              {{ resumeAttaches.fileName }}
            </p>
            <p class="clickable blue text-overflow long-overflow" v-else @click="onClickMyResume(user.resumeId, user.delYn)">
              {{ user.title }}
            </p>
          </td>
          <td>{{ user.interests }}</td>
          <td>{{ $gbNameKR('MAJOR_GB', user.majorGb) }}</td>
          <td>
            <p class="clickable blue text-overflow" @click="onClickJobNotice(user.jobNoticeSeq)">{{ user.jobName }}</p>
          </td>
          <td>{{ $yyyymmddHHmmss(user.createDate) }}</td>
          <td>
            <span v-if="user.intvPropStateGb === '01'">{{ $gbNameKR('ADMIN_INTV_PROP_STATE_GB',user.intvPropStateGb) }}</span>  
            <span v-else-if="user.intvPropStateGb === '02' || user.intvPropStateGb === '05'" class="blue">{{ $gbNameKR('ADMIN_INTV_PROP_STATE_GB',user.intvPropStateGb) }}</span>
            <span v-else-if="user.intvPropStateGb === '03' || user.intvPropStateGb === '06'" class="red">{{ $gbNameKR('ADMIN_INTV_PROP_STATE_GB',user.intvPropStateGb) }}</span>
            <span v-else class="gray">{{ $gbNameKR('ADMIN_INTV_PROP_STATE_GB',user.intvPropStateGb) }}</span>
          </td>
          <td>
            <span v-if="user.intvPropStateGb === '01'">
              <KSATextButton class="clickable black" text="제안취소" @click="onClickIntvPropState(user.resumeId, user.jobNoticeSeq, '04')"/>
            </span>
            <span v-else-if="user.intvPropStateGb === '02'">
              <KSATextButton class="clickable black" text="최종합격" @click="onClickIntvPropState(user.resumeId, user.jobNoticeSeq, '05')"/>
              &nbsp;
              <KSATextButton class="clickable black" text="최종불합격" @click="onClickIntvPropState(user.resumeId, user.jobNoticeSeq, '06')"/>
            </span>
            <span v-else>
              -
            </span>
          </td>
        </tr>
      </table>
    </div>
    <div class="result-none lecture-none" v-if="list.length === 0">
      보낸 면접제안 내역이 없습니다.
    </div>
    <Paginate
        v-if="list.length > 0"
        v-model="currentPage"
        :page-count="totalPages"
        :click-handler="onSelectPage"
    />
  </div>
  <JobFairResumeDetailModal ref="jobFairResumeModal"/> <!-- 특별채용관 이력서 상세 -->
  <JobFairJobNoticeDetailModal ref="jobNoticeModal"/> <!-- 채용공고 상세 -->
</template>

<style lang="scss" scoped>
  .text-overflow {
    max-width: 170px;
  }
</style>
